import React, { useState, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Search, MoreVertical, ChevronLeft, ChevronRight, Loader } from "lucide-react";
import Dropdown from "./ui/Dropdown";
import { useAllUsers } from "../services/queries/queries";
import Pagination from "./ui/Pagination";
import { useNavigate } from "react-router-dom";

function UserTable() {
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState("");
  const [pageNum, setPageNum] = useState(1);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilter(globalFilter);
      if (globalFilter !== debouncedFilter) {
        setPageNum(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [globalFilter, debouncedFilter]);

  const { data, isError, isLoading, error } = useAllUsers(pageNum, limit, debouncedFilter);
  const navigate = useNavigate();

  const columns = [
    {
      accessorFn: (row) => `${row.firstName} ${row.lastName}`,
      header: "Full Name",
      cell: ({ row }) => (
        <span className="min-w-[150px] inline-block">
          {row.original.firstName} {row.original.lastName}
        </span>
      ),
    },
    {
      accessorFn: (row) => `${row.email} `,
      header: "Email",
    },
    {
      accessorFn: (row) => `${row.phoneNumber}`,
      header: "Phone Number",
    },
    {
      accessorKey: "country",
      header: "Country",
      accessorFn: (row) => `${row.country}`,
      cell: ({ row }) => (
        <span className="min-w-[150px] inline-block">
          {row.original.country}
        </span>
      ),
    },
    {
      accessorFn: (row) => (row.isEmailVerified ? "Verified" : "Unverified"),
      header: "Email Status",
      cell: (info) => {
        const status = info.getValue();
        const statusStyles = {
          Verified: "bg-green-100 text-green-800",
          Unverified: "bg-yellow-100 text-yellow-800",
        };

        return (
          <span
            className={`px-5 py-2 text-sm font-medium rounded-xl ${statusStyles[status]}`}
          >
            {status}
          </span>
        );
      },
    },
    {
      accessorKey: "created",
      header: "Created",
      cell: ({ row }) =>
        row.original.created
          ? new Date(row.original.created).toLocaleDateString()
          : "--",
    },
    {
      accessorKey: "deletedAt",
      header: "Deleted",
      cell: ({ row }) =>
        row.original.deletedAt ? (
          <span className="px-3 py-1 text-sm font-medium text-red-800 bg-red-100 rounded-xl">
            {new Date(row.original.deletedAt).toLocaleDateString()}
          </span>
        ) : (
          "--"
        ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <Dropdown
          trigger={<MoreVertical className="w-4 h-4" />}
          rowIndex={row.index}
          items={[
            {
              label: "View Details",
              onClick: () => navigate(`/users/${row.original.uuid}`),
            },
            {
              label: "Edit User",
              onClick: () => navigate(`/users/${row.original.uuid}/edit`),
            },
            {
              label: "Deactivate",
              onClick: () => { },
              className: "text-red-600",
            },
          ]}
        />
      ),
    },
  ];

  const tableData = React.useMemo(() => data?.data || [], [data]);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  });

  const handleSearch = (e) => {
    const value = e.target.value;
    setGlobalFilter(value);
  };

  // if (isLoading) {
  //   return (
  //     <div className="flex h-screen">
  //       <div className="flex flex-1 justify-center items-center">
  //         <p className="text-gray-600">Loading user data...</p>
  //       </div>
  //     </div>
  //   );
  // }

  if (isError) {
    return (
      <div className="flex h-screen">
        <div className="flex flex-1 justify-center items-center">
          <p className="text-red-600">Error: {error.message}</p>
        </div>
      </div>
    );
  }


  return (
    <div className="bg-white rounded-lg">
      <div className="flex justify-between mb-6">
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search"
            className="py-3 pr-4 pl-10 w-full rounded-3xl border focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            value={globalFilter}
            onChange={handleSearch}
          />
        </div>
      </div>

      {
        isLoading ? (
          <div className="flex h-[400px]">
            <div className="flex flex-1 justify-center items-center">
              <p className="flex gap-2 items-center font-medium text-gray-600">Loading consumers data <Loader className="w-5 h-5 animate-spin" /></p>
            </div>
          </div>
        ) : (
          <div className="overflow-hidden rounded border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className="border-b">
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className={`px-4 py-4 text-sm font-bold text-left text-gray-500 md:px-6 md:py-4 bg-gray-50/50`}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.length > 0 ? (
                    table.getRowModel().rows.map((row) => (
                      <tr
                        key={row.id}
                        className="border-b hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/users/${row.original.uuid}`)}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className="px-4 py-4 md:px-6 md:py-7 text-sm text-[#333333] font-sans"
                            onClick={
                              cell.column.id === "actions"
                                ? (e) => e.stopPropagation()
                                : undefined
                            }
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="px-6 py-8 text-center text-gray-500"
                      >
                        No records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )
      }

      {table.getRowModel().rows.length > 0 && (
        <div className="flex flex-col gap-4 justify-between items-center p-3 border border-gray-200 md:p-6 mdborder bg-gray-50/50 md:flex-row">
          <div className="text-sm text-gray-600">
            Showing <span className="font-medium">{(pageNum - 1) * limit + 1}</span> to{" "}
            <span className="font-medium">
              {Math.min(pageNum * limit, data?.meta?.totalItems || 0)}
            </span>{" "}
            of <span className="font-medium">{data?.meta?.totalItems || 0}</span> entries
          </div>
          <Pagination
            currentPage={pageNum}
            totalPages={data?.meta?.totalPages || 1}
            onPageChange={(page) => setPageNum(page)}
          />
        </div>
      )}
    </div>
  );
}

export default UserTable;
